import { useEffect, useState } from "react";
import { getApiBaseUrl } from "../utils/apiAssetUrl";
import Header from "./Header";
import { useToast } from "@chakra-ui/react";
import {
  Box,
  Button,
  Flex,
  Heading,
  HStack,
  Input,
  Select,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  Badge,
  IconButton,
  useDisclosure,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  VStack,
} from "@chakra-ui/react";
import { PhoneIcon, EmailIcon } from "@chakra-ui/icons";

/** `label` = modal/full title; `header` = short table head (avoids crushed ALL-CAPS wrapping); `minW` = column floor for auto table layout */
const LEAD_COLUMNS = [
  { key: "name", label: "Name", header: "Name", minW: "128px" },
  { key: "email", label: "Email", header: "Email", minW: "200px" },
  { key: "phone", label: "Phone", header: "Phone", minW: "124px" },
  { key: "category", label: "Category", header: "Category", minW: "100px" },
  { key: "store_name", label: "Store", header: "Store", minW: "168px" },
  { key: "type", label: "Type", header: "Type", minW: "88px" },
  { key: "production_name", label: "Project Name", header: "Project", minW: "160px" },
  { key: "hirer_name", label: "Hirer (Client)", header: "Hirer", minW: "132px" },
  { key: "authorized_contact", label: "Authorized Contact", header: "Auth. contact", minW: "132px" },
  { key: "project_type", label: "Project Type", header: "Proj. type", minW: "112px" },
  { key: "reporting_datetime", label: "Reporting Date & Time", header: "Reporting", minW: "156px" },
  { key: "completion_date", label: "Completion Date", header: "Completion", minW: "120px" },
  { key: "total_units", label: "Total Units/Shifts", header: "Units / shifts", minW: "120px" },
  { key: "location_address", label: "Location Address", header: "Location", minW: "280px" },
  { key: "status", label: "Status", header: "Status", minW: "152px" },
  { key: "createdAt", label: "Date", header: "Created", minW: "148px" },
];

const LEADS_TABLE_MIN_WIDTH_PX =
  LEAD_COLUMNS.reduce((sum, c) => sum + (parseInt(String(c.minW).replace(/px/gi, "").trim(), 10) || 0), 0) + 108;

const MODAL_EXTRA_FIELDS = [
  { key: "user_name", label: "Platform user (login)" },
  { key: "user_id", label: "Platform user ID" },
  { key: "vendor_name", label: "Vendor / listing name" },
  { key: "vendor_phone", label: "Vendor phone" },
  { key: "vendor_permanentId", label: "Vendor permanent ID" },
  { key: "store_id", label: "Store ID" },
  { key: "connected_time", label: "Last connected" },
];

function parseStoreBusinessDetails(store) {
  if (!store?.businessDetails) return null;
  const bd = store.businessDetails;
  if (typeof bd === "string") {
    try {
      return JSON.parse(bd || "{}");
    } catch {
      return null;
    }
  }
  return bd;
}

/** Same rules as DetailPageVendor `isFreePlan` — pricing lives on `planObject`, not top-level `sale_price`. */
function parseStorePlanObject(storeRow) {
  if (!storeRow || typeof storeRow !== "object") return {};
  const raw = storeRow.planObject;
  if (raw == null) return {};
  if (typeof raw === "string") {
    try {
      return JSON.parse(raw || "{}");
    } catch {
      return {};
    }
  }
  return raw;
}

function isVendorOnFreePlan(storeRow) {
  if (storeRow == null) return true;
  const po = parseStorePlanObject(storeRow);
  if (!po || Object.keys(po).length === 0) return true;
  const sale = Number(po.sale_price);
  return !Number.isFinite(sale) || sale <= 0;
}

const ALL_ROWS_PAGE_SIZE = 100000;

function Leads() {
  const [leads, setLeads] = useState([]);
  const [filteredLeads, setFilteredLeads] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [search, setSearch] = useState("");
  const [filterType, setFilterType] = useState("all");
  const [customFrom, setCustomFrom] = useState("");
  const [customTo, setCustomTo] = useState("");
  const login = JSON.parse(localStorage.getItem("login")) || "";
  const toast = useToast();
  const [plan, setPlan] = useState({});
  const { isOpen: isDetailOpen, onOpen: openDetail, onClose: closeDetail } = useDisclosure();
  const [selectedLead, setSelectedLead] = useState(null);

  const url = getApiBaseUrl();

  const getDisplayValue = (lead, key) => {
    if (key === "store_name") {
      const biz = parseStoreBusinessDetails(lead.store);
      return biz?.businessName || lead.store_name || "—";
    }
    if (key === "category") {
      return lead.store?.category || lead.category || "—";
    }
    if (key === "createdAt" || key === "connected_time") {
      const raw = lead[key];
      if (!raw) return "—";
      return new Date(raw).toLocaleString("en-IN", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        hour12: true,
      });
    }
    if (key === "type") {
      return lead.type || "call";
    }
    const v = lead[key];
    if (v === null || v === undefined || v === "") return "—";
    return String(v);
  };

  const parseLeadStore = (lead) => {
    if (!lead?.store) return null;
    if (typeof lead.store === "string") {
      try {
        return JSON.parse(lead.store);
      } catch {
        return null;
      }
    }
    return lead.store;
  };

  const fetchLead = async () => {
    try {
      const response = await fetch(`${url}/leads/leadsnew/${login.permanent_ID}`);
      const res = await response.json();
      if (res.isError) {
        toast({
          title: "Could not load leads",
          description: res.error || res.message || "Unknown error",
          status: "error",
          duration: 5000,
          position: "top-center",
          isClosable: true,
        });
        setLeads([]);
        setFilteredLeads([]);
        return;
      }
      const rawList = res.user ?? res.leads ?? [];
      const list = Array.isArray(rawList) ? rawList : [];
      const parsed = list.map((lead) => ({
        ...lead,
        store: parseLeadStore(lead),
      }));
      setLeads(parsed);
      setFilteredLeads(parsed);
    } catch (err) {
      console.log(err);
      toast({
        title: "Network error",
        description: err?.message || "Failed to fetch leads",
        status: "error",
        duration: 5000,
        position: "top-center",
        isClosable: true,
      });
    }
  };

  useEffect(() => {
    fetchLead();
  }, []);

  const planData = async () => {
    try {
      const response = await fetch(`${url}/store/getstorelist/mystore/${login.permanent_ID}`);
      const res = await response.json();
      setPlan(res);
    } catch (error) {
      console.log(error);
    }
  };
  useEffect(() => {
    planData();
  }, []);

  const applyFilters = () => {
    let result = [...leads];
    if (search.trim() !== "") {
      const q = search.toLowerCase();
      result = result.filter((lead) => {
        const biz = parseStoreBusinessDetails(lead.store);
        const storeName = (biz?.businessName || lead.store_name || "").toLowerCase();
        const bookingHay = [
          lead.production_name,
          lead.hirer_name,
          lead.location_address,
          lead.project_type,
          lead.reporting_datetime,
          lead.authorized_contact,
        ]
          .filter(Boolean)
          .join(" ")
          .toLowerCase();
        return (
          lead.name?.toLowerCase().includes(q) ||
          lead.phone?.includes(search) ||
          lead.email?.toLowerCase().includes(q) ||
          storeName.includes(q) ||
          bookingHay.includes(q)
        );
      });
    }
    const now = new Date();
    const applyLastDays = (days) =>
      result.filter((lead) => {
        const diff = (now - new Date(lead.createdAt)) / (1000 * 3600 * 24);
        return diff <= days;
      });
    switch (filterType) {
      case "today":
        result = result.filter((l) => new Date(l.createdAt).toDateString() === now.toDateString());
        break;
      case "yesterday":
        let y = new Date();
        y.setDate(y.getDate() - 1);
        result = result.filter((l) => new Date(l.createdAt).toDateString() === y.toDateString());
        break;
      case "7":
      case "14":
      case "28":
      case "60":
      case "90":
        result = applyLastDays(parseInt(filterType));
        break;
      case "custom":
        if (customFrom && customTo) {
          const from = new Date(customFrom);
          const to = new Date(customTo);
          result = result.filter((l) => {
            const d = new Date(l.createdAt);
            return d >= from && d <= to;
          });
        }
        break;
      default:
        break;
    }
    setFilteredLeads(result);
  };

  useEffect(() => {
    applyFilters();
  }, [search, filterType, customFrom, customTo, leads]);

  const updateStatusInBackend = async (id, newStatus) => {
    try {
      const response = await fetch(`${url}/leads/update-status`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ id, status: newStatus }),
      });
      const res = await response.json();
      if (response.ok) {
        toast({ description: "Status updated", status: "success", duration: 5000, position: "top-center", isClosable: true });
        setLeads((prev) => prev.map((lead) => (lead.id === id ? { ...lead, status: newStatus } : lead)));
      } else {
        toast({ description: res.message || "Failed", status: "error", duration: 5000, position: "top-center", isClosable: true });
      }
    } catch (error) {
      toast({ description: error.message, status: "error", duration: 5000, position: "top-center", isClosable: true });
    }
  };

  const saveConnectedTime = async (leadId, type) => {
    try {
      await fetch(`${url}/leads/leadsnew/update-connected-time`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ id: leadId, type }),
      });
      setLeads((prev) =>
        prev.map((lead) => (lead.id === leadId ? { ...lead, connected_time: new Date().toISOString() } : lead))
      );
    } catch (err) {
      console.log(err);
    }
  };

  const effectivePageSize = Math.min(itemsPerPage, ALL_ROWS_PAGE_SIZE);
  const indexOfLastItem = currentPage * effectivePageSize;
  const indexOfFirstItem = indexOfLastItem - effectivePageSize;
  const currentItems = filteredLeads.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.max(1, Math.ceil(filteredLeads.length / effectivePageSize));
  const rowsPerPageSelectValue =
    itemsPerPage >= ALL_ROWS_PAGE_SIZE ? "all" : [10, 25, 50, 100].includes(itemsPerPage) ? String(itemsPerPage) : "10";

  const isFree = isVendorOnFreePlan(plan);

  return (
    <>
      <Header />
      <Box minH="100vh" bg="gray.50" py={8} px={{ base: 4, md: 8 }}>
        <Box maxW="100%" w="100%" mx="auto">
          <Heading size="lg" mb={6} color="gray.800" fontWeight="700">
            Leads
          </Heading>

          <Box bg="white" borderRadius="xl" boxShadow="0 4px 24px rgba(0,0,0,0.06)" overflow="hidden" mb={6}>
            <Flex p={4} gap={4} wrap="wrap" align="center" borderBottom="1px solid" borderColor="gray.100">
              <Input
                placeholder="Search by name, phone, email..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                maxW="320px"
                size="md"
                borderRadius="lg"
                borderColor="gray.200"
                _focus={{ borderColor: "brand.500", boxShadow: "0 0 0 1px var(--chakra-colors-brand-500)" }}
              />
              <Select
                maxW="180px"
                value={filterType}
                onChange={(e) => setFilterType(e.target.value)}
                borderRadius="lg"
                borderColor="gray.200"
              >
                <option value="all">All</option>
                <option value="today">Today</option>
                <option value="yesterday">Yesterday</option>
                <option value="7">Last 7 Days</option>
                <option value="14">Last 14 Days</option>
                <option value="28">Last 28 Days</option>
                <option value="60">Last 60 Days</option>
                <option value="90">Last 90 Days</option>
                <option value="custom">Custom</option>
              </Select>
              {filterType === "custom" && (
                <HStack>
                  <Input type="date" value={customFrom} onChange={(e) => setCustomFrom(e.target.value)} size="sm" maxW="150px" />
                  <Input type="date" value={customTo} onChange={(e) => setCustomTo(e.target.value)} size="sm" maxW="150px" />
                </HStack>
              )}
            </Flex>

            <Flex px={4} py={2} borderBottom="1px solid" borderColor="gray.100" align="center" gap={3} flexWrap="wrap">
              <Text fontSize="sm" color="gray.600" whiteSpace="nowrap">
                Rows per page
              </Text>
              <Select
                size="sm"
                maxW="100px"
                value={rowsPerPageSelectValue}
                onChange={(e) => {
                  const v = e.target.value;
                  setItemsPerPage(v === "all" ? ALL_ROWS_PAGE_SIZE : Number(v));
                  setCurrentPage(1);
                }}
              >
                <option value="10">10</option>
                <option value="25">25</option>
                <option value="50">50</option>
                <option value="100">100</option>
                <option value="all">All</option>
              </Select>
            </Flex>

            <Box
              overflowX="auto"
              maxH={{ base: "none", md: "72vh" }}
              overflowY="auto"
              sx={{
                WebkitOverflowScrolling: "touch",
                "& table": { tableLayout: "auto" },
              }}
            >
              <Table
                size="sm"
                variant="simple"
                minW={`${LEADS_TABLE_MIN_WIDTH_PX}px`}
                sx={{ tableLayout: "auto" }}
              >
                <Thead bg="brand.600" position="sticky" top={0} zIndex={1} boxShadow="0 1px 0 rgba(0,0,0,0.08)">
                  <Tr>
                    {LEAD_COLUMNS.map((col) => (
                      <Th
                        key={col.key}
                        color="white"
                        fontWeight="700"
                        fontSize="11px"
                        textTransform="uppercase"
                        lineHeight="1.3"
                        letterSpacing="0.04em"
                        whiteSpace="nowrap"
                        minW={col.minW}
                        px={3}
                        py={3.5}
                        borderBottom="2px solid"
                        borderColor="blackAlpha.200"
                      >
                        {col.header}
                      </Th>
                    ))}
                    <Th
                      color="white"
                      fontWeight="700"
                      fontSize="11px"
                      textTransform="uppercase"
                      lineHeight="1.3"
                      letterSpacing="0.04em"
                      whiteSpace="nowrap"
                      minW="100px"
                      w="100px"
                      px={3}
                      py={3.5}
                      borderBottom="2px solid"
                      borderColor="blackAlpha.200"
                    >
                      Actions
                    </Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {currentItems.length === 0 ? (
                    <Tr>
                      <Td colSpan={LEAD_COLUMNS.length + 1} py={12} textAlign="center" color="gray.500">
                        No leads found
                      </Td>
                    </Tr>
                  ) : (
                    currentItems.map((lead) => (
                      <Tr
                        key={lead.id}
                        _hover={{ bg: "gray.50" }}
                        cursor="pointer"
                        onClick={() => {
                          setSelectedLead(lead);
                          openDetail();
                        }}
                      >
                        {LEAD_COLUMNS.map((col) => (
                          <Td
                            key={col.key}
                            fontSize="sm"
                            py={3}
                            px={3}
                            verticalAlign="top"
                            minW={col.minW}
                            borderBottom="1px solid"
                            borderColor="gray.100"
                            sx={{ wordBreak: "break-word", overflowWrap: "break-word" }}
                          >
                            {col.key === "status" ? (
                              <Select
                                value={lead.status || "Pending"}
                                size="sm"
                                minW="142px"
                                maxW="160px"
                                isDisabled={isFree}
                                onClick={(e) => e.stopPropagation()}
                                onChange={(e) => updateStatusInBackend(lead.id, e.target.value)}
                              >
                                <option value="Pending">Pending</option>
                                <option value="Cancelled">Cancelled</option>
                                <option value="Converted">Converted</option>
                                <option value="Not Interested">Not Interested</option>
                                <option value="In Progress">In Progress</option>
                              </Select>
                            ) : col.key === "type" ? (
                              <Badge
                                colorScheme={lead.type === "booking" ? "purple" : "blue"}
                                fontSize="xs"
                                px={2}
                                py={0.5}
                                whiteSpace="nowrap"
                              >
                                {getDisplayValue(lead, col.key)}
                              </Badge>
                            ) : (
                              <Text
                                whiteSpace="normal"
                                wordBreak="break-word"
                                lineHeight="1.5"
                                fontSize="sm"
                                title={String(getDisplayValue(lead, col.key))}
                              >
                                {getDisplayValue(lead, col.key)}
                              </Text>
                            )}
                          </Td>
                        ))}
                        <Td
                          py={3}
                          px={3}
                          verticalAlign="top"
                          minW="100px"
                          borderBottom="1px solid"
                          borderColor="gray.100"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <HStack spacing={1}>
                            <IconButton
                              aria-label="Call"
                              size="sm"
                              colorScheme="gray"
                              variant="outline"
                              icon={<PhoneIcon />}
                              isDisabled={isFree}
                              onClick={() => {
                                saveConnectedTime(lead.id, "call");
                                window.location.href = `tel:${lead.phone}`;
                              }}
                            />
                            <IconButton
                              aria-label="Email"
                              size="sm"
                              colorScheme="gray"
                              variant="outline"
                              icon={<EmailIcon />}
                              isDisabled={isFree}
                              onClick={() => {
                                saveConnectedTime(lead.id, "email");
                                window.location.href = `mailto:${lead.email}`;
                              }}
                            />
                          </HStack>
                        </Td>
                      </Tr>
                    ))
                  )}
                </Tbody>
              </Table>
            </Box>
          </Box>

          <Flex justify="space-between" align="center" mt={4}>
            <Text fontSize="sm" color="gray.600">
              Page {currentPage} of {totalPages} ({filteredLeads.length} leads)
            </Text>
            <HStack>
              <Button
                size="sm"
                variant="outline"
                isDisabled={currentPage <= 1}
                onClick={() => setCurrentPage((p) => p - 1)}
              >
                Prev
              </Button>
              <Button
                size="sm"
                variant="outline"
                isDisabled={currentPage >= totalPages}
                onClick={() => setCurrentPage((p) => p + 1)}
              >
                Next
              </Button>
            </HStack>
          </Flex>
        </Box>
      </Box>

      <Modal isOpen={isDetailOpen} onClose={closeDetail} size="xl" isCentered scrollBehavior="inside">
        <ModalOverlay />
        <ModalContent borderRadius="xl" maxH="90vh">
          <ModalHeader bg="brand.600" color="white" borderTopRadius="xl">
            Lead details
            {selectedLead?.type === "booking" ? (
              <Badge ml={2} colorScheme="purple" verticalAlign="middle">
                Book now
              </Badge>
            ) : null}
          </ModalHeader>
          <ModalCloseButton color="white" />
          <ModalBody py={6} overflowY="auto">
            {selectedLead && (
              <VStack align="stretch" spacing={3}>
                {LEAD_COLUMNS.map((col) => (
                  <Flex key={col.key} direction={{ base: "column", sm: "row" }} gap={1} align="start">
                    <Text fontWeight="600" color="gray.600" fontSize="sm" minW={{ sm: "200px" }} flexShrink={0}>
                      {col.label}
                    </Text>
                    <Text fontSize="sm" flex={1} wordBreak="break-word" whiteSpace="pre-wrap">
                      {col.key === "type" ? (
                        <Badge colorScheme={selectedLead.type === "booking" ? "purple" : "blue"}>
                          {getDisplayValue(selectedLead, col.key)}
                        </Badge>
                      ) : (
                        getDisplayValue(selectedLead, col.key)
                      )}
                    </Text>
                  </Flex>
                ))}
                {MODAL_EXTRA_FIELDS.map((col) => (
                  <Flex key={col.key} direction={{ base: "column", sm: "row" }} gap={1} align="start">
                    <Text fontWeight="600" color="gray.600" fontSize="sm" minW={{ sm: "200px" }} flexShrink={0}>
                      {col.label}
                    </Text>
                    <Text fontSize="sm" flex={1} wordBreak="break-word" whiteSpace="pre-wrap">
                      {getDisplayValue(selectedLead, col.key)}
                    </Text>
                  </Flex>
                ))}
              </VStack>
            )}
          </ModalBody>
        </ModalContent>
      </Modal>
    </>
  );
}

export default Leads;
